import dayjs from 'dayjs';

const normalizeTimestampToMs = (timestamp: string): number => {
  const parsedTimestamp = parseInt(timestamp, 10);
  const timestampLength = String(Math.abs(parsedTimestamp)).length;

  if (timestampLength <= 13) {
    return parsedTimestamp;
  }

  return Math.floor(parsedTimestamp / 10 ** (timestampLength - 13));
};

export const normalizeTimeTokenTo17Digits = (timestamp: string): string => {
  const parsedTimestamp = parseInt(timestamp, 10);
  const timestampLength = String(Math.abs(parsedTimestamp)).length;

  if (timestampLength === 17) {
    return String(parsedTimestamp);
  }

  if (timestampLength > 17) {
    return String(Math.floor(parsedTimestamp / 10 ** (timestampLength - 17)));
  }

  return String(parsedTimestamp * 10 ** (17 - timestampLength));
};

export const displayDateTime = (
  timestamp: string,
  type?: 'short' | 'date' | 'time'
) => {
  try {
    const dateTime = new Date(normalizeTimestampToMs(timestamp));
    const dt = dateTime.toISOString();
    const date = new Date(dt);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    };

    const dateOptions: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    };

    switch (type) {
      case 'time':
        return date.toLocaleString('en-US', timeOptions);
      case 'short':
        if (isToday) return date.toLocaleString('en-US', timeOptions);
        if (isYesterday) return 'Yesterday';
        return date.toLocaleString('en-US', {
          ...dateOptions,
          year: undefined,
        });
      case 'date':
        if (isToday) return 'Today';
        if (isYesterday) return 'Yesterday';
        return date.toLocaleString('en-GB', dateOptions);
      default:
        if (isToday || isYesterday)
          return (
            (isToday ? 'Today at ' : 'Yesterday at ') +
            date.toLocaleString('en-US', timeOptions)
          );
        return `${date.toLocaleString(
          'en-US',
          dateOptions
        )} at ${date.toLocaleString('en-US', timeOptions)}`;
    }
  } catch (e) {
    return 'invalid date';
  }
};

export const timeDiffIs24Hrs = (timestamp: string) => {
  const dateTime = new Date(normalizeTimestampToMs(timestamp)).getTime();
  const currentDate = new Date().getTime();
  const timeDifference = Math.abs(currentDate - dateTime);
  const millisecondsIn24Hours = 24 * 60 * 60 * 1000;
  const is24HoursDifference = timeDifference === millisecondsIn24Hours;
  return is24HoursDifference;
};

export const convertUTCToIST = (dateStr: string) => {
  const date = new Date(dateStr);
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istTime = new Date(date.getTime() + istOffset);
  const istDateStr = istTime.toLocaleString('en-US', {
    timeZone: 'Asia/Kolkata',
  });
  return istDateStr;
};

export const areDatesEqual = (date1: Date, date2: Date): boolean => {
  return date1.getTime() === date2.getTime();
};

interface DateRange {
  startDate: string;
  endDate: string;
}
export const getRange = ({ startDate, endDate }: DateRange): string => {
  const s = new Date(startDate),
    e = new Date(endDate);
  const days = Math.floor((+e - +s) / 86_400_000) + 1;
  const fmt = (d: Date) =>
    [d.getDate(), d.getMonth() + 1, d.getFullYear()]
      .map((n) => String(n).padStart(2, '0'))
      .join('/');
  return `(${fmt(s)} - ${fmt(e)}) - ${days} Days`;
};

export const findFirstValidDate = (
  start: any,
  end: any,
  blockedDates: string[]
): Date | null => {
  if (!start || !end) return null;
  const blocked = new Set<string>(blockedDates);
  let cursor = dayjs(start).startOf('day');
  const last = dayjs(end).startOf('day');

  while (cursor.isBefore(last, 'day') || cursor.isSame(last, 'day')) {
    const key = cursor.format('DD/MM/YYYY');
    if (!blocked.has(key)) {
      return cursor.toDate();
    }
    cursor = cursor.add(1, 'day');
  }

  return null;
};

export const getValidDates = (
  start: any,
  end: any,
  blockedDates: string[]
): string[] => {
  if (!start || !end) return [];

  const blocked = new Set(blockedDates);
  let cursor = dayjs(start).startOf('day');
  const last = dayjs(end).startOf('day');
  const result: string[] = [];

  while (cursor.isBefore(last, 'day') || cursor.isSame(last, 'day')) {
    const key = cursor.format('DD/MM/YYYY');
    if (!blocked.has(key)) {
      result.push(cursor.toISOString());
    }
    cursor = cursor.add(1, 'day');
  }

  return result;
};

export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;

  // Check if the date is yesterday
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return 'Yesterday';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
